import {
  CONTENTFUL_SPACE_IDS,
  getContentfulClient,
} from '@utils/contentful/clients'
import { IPageDataService } from '..'
import { ContentfulClientApi, Entry } from 'contentful'
import { mapContentfulImageAsset } from '@utils/contentful/contentfulImage'
import { GenericContentBlock } from 'types/contentful/genericContentBlock'
import parentLogger from '@utils/logger'
import { IImage } from '@utils/contentful/contentfulImage'
import * as Sentry from '@sentry/nextjs'
import { IContentfulPage } from '@utils/contentful/contentfulComponents/pageContent'
import { contentfulPageToComponentsProps } from '@utils/contentful/contentfulPageToComponentsProps'
import { parseProductEntry } from './parsers/parceProductEntry'
import { getLocale } from '@utils/contentful'
import parseSharedPageData from './parsers/parseSharedPageData'
import { CONTENTFUL_BASE_URL } from '@utils/client-side-config'
import { EditorContentfulConfiguration } from 'app/(main)/(catalog)/upload/[slug]/_components/designUpload/types'
import { CategoryPage } from 'types/contentful/categoryPage'
import { IPlusEditorContentfulConfiguration } from 'app/(main)/(catalog)/quote/[slug]/_types'
import { ICartSidebar } from 'types/contentful/cartSidebar'

const CONTENTFUL_ENVIRONMENT = process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT
const CONTENTFUL_ACCESS_TOKEN = process.env.NEXT_PUBLIC_CONTENTFUL_ACCESS_TOKEN
const CONETNTFUL_CACHE_PERIOD = 1800

const REVALIDATE_TIME = {
  SHARED_PAGE_DATA: 900, // 15 minutes
  PRODUCT_ENTRIES: 1800, // 30 minutes
  PAGE_COMPONENTS: 1800, // 30 minutes

  NAVIGATION_ENTRIES: 7200, // 2 hours
  EDITORS: 7200, // 2 hours
  CATEGORY_PAGE: 7200, // 2 hours

  CART_SIDEBAR: 18000, // 5 hours
  CROSS_SELL_OVERRIDE: 1800, // 5 hours
  FOOTER_ASSETS: 18000, // 5 hours
}

/**
 * When using fetch instead of the Contentful client npm package, references are stored in separate fields in the response object.
 * To match the behavior of the Contentful client, we need to resolve these references by replacing the links with their actual data.
 */
const resolveContentfulReferences = (contentfulResponse: any) => {
  const linkedEntries = contentfulResponse?.includes?.Entry
  const linkedAssets = contentfulResponse?.includes?.Asset

  if (!linkedEntries && !linkedAssets) {
    return contentfulResponse?.items
  }

  const resolveReferences = (contentfulField: any) => {
    if (Array.isArray(contentfulField)) {
      return contentfulField.map(resolveReferences)
    }

    if (
      contentfulField?.sys?.type === 'Link' &&
      contentfulField?.sys?.linkType === 'Entry'
    ) {
      const linkedEntry = linkedEntries?.find(
        (entry: any) => entry.sys.id === contentfulField.sys.id
      )

      if (!linkedEntry) {
        return null
      }

      return {
        ...linkedEntry,
        fields: processContentfulFields(linkedEntry?.fields),
      }
    }

    if (
      contentfulField?.sys?.type === 'Link' &&
      contentfulField?.sys?.linkType === 'Asset'
    ) {
      const linkedAsset = linkedAssets.find(
        (asset: any) => asset.sys.id === contentfulField.sys.id
      )
      return linkedAsset
    }

    // Is not a link, return the field as is
    return contentfulField
  }

  const processContentfulFields = (fields: any) => {
    if (!fields) {
      return null
    }

    Object.entries(fields).forEach(([key, value]) => {
      fields[key] = resolveReferences(value)
    })

    return fields
  }

  return contentfulResponse?.items?.map((item) => {
    const rootEntryFields = item?.fields
    return {
      fields: processContentfulFields(rootEntryFields),
      sys: item?.sys,
    }
  })
}

export class ContentfulService implements IPageDataService {
  private readonly ecommerceOneClient: ContentfulClientApi
  private readonly ecommerceTwoClient: ContentfulClientApi
  private readonly ecommerceOneFetch: (
    query: string,
    customRevalidateTime?: number
  ) => Promise<{ items: { fields: any; sys: { id: string } }[] | undefined }>

  constructor() {
    this.ecommerceOneClient = getContentfulClient({
      contentfulSpace: CONTENTFUL_SPACE_IDS.ECOMMERCE_1,
    })
    this.ecommerceTwoClient = getContentfulClient({
      contentfulSpace: CONTENTFUL_SPACE_IDS.ECOMMERCE_2,
    })

    this.ecommerceOneFetch = async (
      query: string,
      customRevalidateTime?: number
    ) => {
      const fnLogger = parentLogger.child({
        name: 'ecommerceOneFetch',
      })
      const url = `${CONTENTFUL_BASE_URL}/spaces/${CONTENTFUL_SPACE_IDS.ECOMMERCE_1}/environments/${CONTENTFUL_ENVIRONMENT}/entries?${query}`
      const headers = {
        Authorization: `Bearer ${CONTENTFUL_ACCESS_TOKEN}`,
      }
      const nextConfig = {
        revalidate: customRevalidateTime || CONETNTFUL_CACHE_PERIOD,
      }

      const response = await fetch(url, {
        headers,
        next: nextConfig,
      })
      fnLogger.info({ url }, 'Contentful Request With Fetch')

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(
          `Contentful API error: ${response.status} ${response.statusText} - ${errorText}`
        )
      }

      const parsedResponse = await response.json()
      const withResolvedReferences = resolveContentfulReferences(parsedResponse)

      return {
        items: withResolvedReferences,
      }
    }
  }

  async getAccountManagerPicture(email: string): Promise<IImage> {
    const fnLogger = parentLogger.child({
      name: 'getAccountManagerPicture',
    })
    fnLogger.info({ email }, 'Getting account manager picture')
    try {
      const entry = await this.getEntriesWithFetch({
        contentType: 'genericContentBlock',
        query: `fields.entryId=${email}`,
      })

      const blockEntry: GenericContentBlock = entry?.items[0]
        ?.fields as GenericContentBlock

      if (!blockEntry) {
        throw new Error("Couldn't find Account Owner Picture!")
      }

      fnLogger.info({ blockEntry }, 'Found account manager picture')
      return mapContentfulImageAsset(blockEntry?.primaryImages?.[0])
    } catch (err) {
      fnLogger.error({ err }, 'Error getting account manager picture')
      Sentry.captureMessage('Error getting account manager picture', {
        contexts: {
          data: {
            email,
          },
        },
      })

      // Fallback to placeholder image
      return {
        imageTitle: 'Account Owner Picture',
        imageUrl: '/account/account-picture-placeholder.png',
      }
    }
  }

  async getProductEntries({
    urlSlug,
    locale,
    productSlug,
  }: {
    urlSlug: string
    locale: string
    productSlug?: string
  }): Promise<any> {
    const fnLogger = parentLogger.child({
      name: 'getProductEntries',
    })
    fnLogger.info({ urlSlug, locale }, 'Getting product entries')

    try {
      fnLogger.time(`getProductEntries ${productSlug || urlSlug}`)
      const productEntries: any = await this.getEntriesWithFetch({
        contentType: 'productCategory',
        query: productSlug
          ? `fields.productSlug=${productSlug}`
          : `fields.urlSlug=${urlSlug}`,
        include: 3,
        customRevalidateTime: REVALIDATE_TIME.PRODUCT_ENTRIES,
      })
      fnLogger.timeEnd(`getProductEntries ${productSlug || urlSlug}`)

      if (!productEntries?.items?.length) {
        fnLogger.warn({ urlSlug }, 'Content not found')
        throw new Error(`Content for ${urlSlug} not found`)
      }

      fnLogger.info(
        { count: productEntries.items.length },
        'Found product entries'
      )
      return parseProductEntry(productEntries?.items?.[0])
    } catch (err) {
      fnLogger.error({ err, urlSlug }, 'Error getting product entries')
      Sentry.captureMessage('Error getting product entries', {
        contexts: {
          data: {
            urlSlug,
            locale,
          },
        },
      })

      return null
    }
  }

  async getPageComponents({
    pageSlug,
    dataForParsers,
  }: {
    pageSlug: string
    dataForParsers: any
  }): Promise<any> {
    const fnLogger = parentLogger.child({
      name: 'getPageComponents',
    })
    fnLogger.info({ pageSlug }, 'Getting page components')

    try {
      fnLogger.time('getPageComponents')
      const locale = getLocale()
      const contentfulPage: {
        items: { fields: IContentfulPage }[] | undefined
      } = await this.ecommerceOneFetch(
        `content_type=noissuePage&fields.slug=${pageSlug}&include=10&locale=${locale}`,
        REVALIDATE_TIME.PAGE_COMPONENTS
      )
      fnLogger.timeEnd('getPageComponents')

      if (!contentfulPage?.items?.length) {
        fnLogger.warn({ pageSlug, locale }, 'Page content not found')
        throw new Error(`Page content for ${pageSlug} not found`)
      }

      const slotAComponentProps = await contentfulPageToComponentsProps({
        contentfulPageComponents:
          contentfulPage?.items[0]?.fields?.configurableListSlotA,
        dataForParsers,
      })

      const slotBComponentProps = await contentfulPageToComponentsProps({
        contentfulPageComponents:
          contentfulPage?.items[0]?.fields?.configurableListSlotB,
        dataForParsers,
      })

      const slotCComponentProps = await contentfulPageToComponentsProps({
        contentfulPageComponents:
          contentfulPage?.items[0]?.fields?.configurableListSlotC,
        dataForParsers,
      })

      /**  We need this JSON hack to remove undefined values from props.
       * Other way next js will throw an erorr for every undefined field.
       * */
      return {
        slotAComponentProps: JSON.parse(JSON.stringify(slotAComponentProps)),
        slotBComponentProps: JSON.parse(JSON.stringify(slotBComponentProps)),
        slotCComponentProps: JSON.parse(JSON.stringify(slotCComponentProps)),
      }
    } catch (err) {
      fnLogger.error({ err, pageSlug }, 'Error getting page components')
      Sentry.captureMessage('Error getting page components', {
        contexts: {
          data: {
            pageSlug,
          },
        },
      })

      return {
        slotAComponentProps: [],
        slotBComponentProps: [],
        slotCComponentProps: [],
      }
    }
  }

  async getEntriesWithFetch({
    contentType,
    query,
    include = 3,
    customRevalidateTime = CONETNTFUL_CACHE_PERIOD,
  }: {
    contentType: string
    query: string
    include?: number
    customRevalidateTime?: number
  }): Promise<{ items: { fields: any }[] | undefined; rawResponse?: any }> {
    const locale = getLocale()

    return this.ecommerceOneFetch(
      `content_type=${contentType}&${query}&locale=${locale}&include=${include}`,
      customRevalidateTime
    )
  }

  async getSharedPageData() {
    const fnLogger = parentLogger.child({
      name: 'getSharedPageData',
    })
    fnLogger.info({}, 'Getting shared page data')

    try {
      fnLogger.time('getSharedPageData')
      const locale = getLocale()

      const sharedPageDataEntry = await this.getEntriesWithFetch({
        contentType: 'sharedPageData',
        query: `fields.slug=shared-page-data`,
        include: 10,
      })
      fnLogger.timeEnd('getSharedPageData')

      const sharedPageDataFields = sharedPageDataEntry?.items?.[0]?.fields
      const sharedPageData = parseSharedPageData(sharedPageDataFields)

      return sharedPageData
    } catch (err) {
      fnLogger.error({ err }, 'Error getting shared page data')
      Sentry.captureException(err, {
        contexts: {
          data: {},
        },
      })

      return null
    }
  }

  async getEditorConfiguration({
    slug,
  }: {
    slug: string
  }): Promise<EditorContentfulConfiguration | null> {
    const fnLogger = parentLogger.child({
      name: 'getEditorConfiguration',
    })
    fnLogger.debug({ slug }, 'Getting editor configuration')

    try {
      const editorConfiguration = await this.getEntriesWithFetch({
        contentType: 'editor',
        query: `fields.urlSlug=${slug}`,
        include: 5,
        customRevalidateTime: REVALIDATE_TIME.EDITORS,
      })

      fnLogger.debug(
        {
          editorConfiguration:
            editorConfiguration?.items?.[0]?.fields?.productName,
        },
        'Editor configuration'
      )

      return editorConfiguration?.items?.[0]
        ?.fields as EditorContentfulConfiguration
    } catch (err) {
      fnLogger.error({ err, slug }, 'Error getting editor configuration')
      Sentry.captureException(err, {
        contexts: {
          data: {
            slug,
          },
        },
      })

      return null
    }
  }

  async getCategoryPageData({
    slug,
  }: {
    slug: string
  }): Promise<CategoryPage | null> {
    const fnLogger = parentLogger.child({
      name: 'getCategoryPageData',
    })
    try {
      fnLogger.info({ slug }, 'Getting category page data')

      const categoryPageEntries = await this.getEntriesWithFetch({
        contentType: 'categoryPage',
        query: `fields.urlSlug=${slug}`,
        include: 10,
        customRevalidateTime: REVALIDATE_TIME.CATEGORY_PAGE,
      })

      fnLogger.debug(
        {
          categoryPageEntries:
            categoryPageEntries?.items?.[0]?.fields?.pageTitleCardTitle,
        },
        'Category page entries'
      )

      return (categoryPageEntries?.items?.[0]?.fields as CategoryPage) || null
    } catch (err) {
      fnLogger.error({ err, slug }, 'Error getting category page data')
      Sentry.captureException(err, {
        contexts: {
          data: { slug },
        },
      })

      return null
    }
  }

  async loadPlusEditorConfig({
    slug,
  }: {
    slug: string
  }): Promise<IPlusEditorContentfulConfiguration | null> {
    const fnLogger = parentLogger.child({
      name: 'loadPlusEditorConfig',
    })
    fnLogger.info({ slug }, 'Loading plus editor config')

    try {
      const editorConfiguration = await this.getEntriesWithFetch({
        contentType: 'plusEditor',
        query: `fields.productSlug=${slug}`,
        include: 5,
        customRevalidateTime: REVALIDATE_TIME.EDITORS,
      })

      fnLogger.debug(
        {
          editorConfiguration:
            editorConfiguration?.items?.[0]?.fields?.productName,
        },
        'Editor configuration'
      )

      return editorConfiguration?.items?.[0]
        ?.fields as IPlusEditorContentfulConfiguration
    } catch (err) {
      fnLogger.error({ err, slug }, 'Error loading plus editor config')
      Sentry.captureException(err, {
        contexts: {
          data: { slug },
        },
      })

      return null
    }
  }

  async getCartSidebar(): Promise<ICartSidebar> {
    const fnLogger = parentLogger.child({
      name: 'getCartSidebar',
    })
    fnLogger.info({}, 'Getting cart sidebar')

    try {
      fnLogger.time('getCartSidebar')
      const cartSidebarEntries = await this.getEntriesWithFetch({
        contentType: 'cartSidebar',
        query: '',
        include: 3,
        customRevalidateTime: REVALIDATE_TIME.CART_SIDEBAR,
      })
      fnLogger.timeEnd('getCartSidebar')

      const cartSidebarData = cartSidebarEntries?.items?.[0]

      return cartSidebarData?.fields as ICartSidebar
    } catch (err) {
      fnLogger.error({ err }, 'Error getting cart sidebar')
      Sentry.captureException(err)
      return null
    }
  }

  async getCrossSellOverride(slug: string): Promise<any> {
    const fnLogger = parentLogger.child({
      name: 'getCrossSellOverride',
    })

    try {
      fnLogger.info({}, 'Getting cross sell override')

      fnLogger.time('getCrossSellOverride')
      const crossSellOverrideEntries = await this.getEntriesWithFetch({
        contentType: 'crossSellOverride',
        query: `fields.slug=${slug}`,
        include: 10,
        customRevalidateTime: REVALIDATE_TIME.CROSS_SELL_OVERRIDE,
      })
      fnLogger.timeEnd('getCrossSellOverride')

      return crossSellOverrideEntries?.items?.[0]?.fields
        ?.crossSellOverrideProducts
    } catch (err) {
      fnLogger.error({ err }, 'Error getting cross sell override')
      Sentry.captureException(err)
      return null
    }
  }

  // Alternative getSharedPageData implementation
  async getEntriesByIds(
    ids: string[],
    include = 5,
    customRevalidateTime = CONETNTFUL_CACHE_PERIOD
  ) {
    const locale = getLocale()

    const entries = await this.ecommerceOneFetch(
      `sys.id[in]=${ids.join(',')}&include=${include}&locale=${locale}`,
      customRevalidateTime
    )

    // Sort items to match the order of the ids array
    const sortedItems = entries?.items?.sort((a, b) => {
      const aIndex = ids.indexOf(a.sys.id)
      const bIndex = ids.indexOf(b.sys.id)
      return aIndex - bIndex
    })

    return sortedItems
  }

  async getShardedSharedPageData() {
    const fnLogger = parentLogger.child({
      name: 'getShardedSharedPageData',
    })

    try {
      fnLogger.time('getShardedSharedPageData')

      const sharedPageData = await this.getEntriesWithFetch({
        contentType: 'sharedPageData',
        query: `fields.slug=shared-page-data`,
        include: 0,
        customRevalidateTime: REVALIDATE_TIME.SHARED_PAGE_DATA,
      })

      const sharedPageDataFields = sharedPageData?.items?.[0]?.fields

      const withLinkEntries = {
        slug: sharedPageDataFields?.slug,
        displayName: sharedPageDataFields?.displayName,
        footerSignupForm: sharedPageDataFields?.footerSignupForm,
        footerColumns: sharedPageDataFields?.footerColumns,
        navbarPrimaryMenuItems: await this.getEntriesByIds(
          sharedPageDataFields?.navbarPrimaryMenuItems?.map((x) => x?.sys?.id)
        ),

        announcementBanner: (
          await this.getEntriesByIds([
            sharedPageDataFields?.announcementBanner?.sys?.id,
          ])
        )?.[0],
        navbarSitewideAnnouncementBar: (
          await this.getEntriesByIds([
            sharedPageDataFields?.navbarSitewideAnnouncementBar?.sys?.id,
          ])
        )?.[0],

        navbarMobileTiles: await this.getEntriesByIds(
          sharedPageDataFields?.navbarMobileTiles?.map((x) => x?.sys?.id)
        ),
        navbarMobileSidebarFooterLinks: await this.getEntriesByIds(
          sharedPageDataFields?.navbarMobileSidebarFooterLinks?.map(
            (x) => x?.sys?.id
          )
        ),

        headerNavbarCategories: await this.getEntriesByIds(
          sharedPageDataFields?.headerNavbarCategories?.map((x) => x?.sys?.id),
          0,
          REVALIDATE_TIME.NAVIGATION_ENTRIES
        ),
        headerNavbarIndustries: await this.getEntriesByIds(
          sharedPageDataFields?.headerNavbarIndustries?.map((x) => x?.sys?.id),
          0,
          REVALIDATE_TIME.NAVIGATION_ENTRIES
        ),

        footerBannerQRcode: (
          await this.getEntriesByIds(
            [sharedPageDataFields?.footerBannerQRcode?.sys?.id],
            5,
            REVALIDATE_TIME.FOOTER_ASSETS
          )
        )?.[0],
        footerBadge: (
          await this.getEntriesByIds(
            [sharedPageDataFields?.footerBadge?.sys?.id],
            5,
            REVALIDATE_TIME.FOOTER_ASSETS
          )
        )?.[0],
      }
      const parsedData = parseSharedPageData(withLinkEntries)

      fnLogger.timeEnd('getShardedSharedPageData')
      return parsedData
    } catch (err) {
      fnLogger.error({ err }, 'Error getting alternative shared page data')
      return null
    }
  }
}
